import React, { useEffect, useState } from 'react'
import CaseCreateHeader from './CaseCreateHeader'
import CaseCreateSidebar from './CaseCreateSidebar'
import CaseCreateFields from './CaseCreateFields'
import CaseCreateFooter from './CaseCreateFooter'
import { formElements, locateField } from '../CaseFormFields'
import FormError from 'Components/Element/FormError'
import FormErrorBanner from 'Components/Element/FormErrorBanner'
import ReviewSection from './ReviewSection'
import useIsMobile from 'Hooks/useIsMobile'
import CaseCreateHeaderMobile from './CaseCreateHeaderMobile'


// Severity rate (step 4) is its own 4-part stepper in the design
// (Physical injury / Economic damages / Non-economic impact / Evidence) —
// all 4 sub-screens stay under the same overall "Step 4 of 5", so a local
// sub-step index drives which of the 4 sections is shown/validated,
// instead of advancing the overall wizard step.
const SEVERITY_STEP = 4;
const severitySections = formElements.filter((element) => element.step === SEVERITY_STEP)?.[0]?.sections || [];

function CreateCaseForm({errors, onChangeUpdate, onBlurValidate, formValues, isFormValid, onSubmit, initialStep, saveDraft, submitError, submitErrorFields, submitAttemptId}) {
    const [currentStep, setCurrentStep] = useState(initialStep || 1);
    const [severitySubStep, setSeveritySubStep] = useState(1);
    const [apiError, setApiError] = useState(false);
    const [isAgreed, setIsAgreed] = useState(false);
    const isMobile = useIsMobile();
    const [err, setErr] = useState(false);
    const [currentSection, setCurrentSection] = useState(
      currentStep === SEVERITY_STEP
        ? (severitySections[severitySubStep - 1] ? [severitySections[severitySubStep - 1]] : [])
        // Falls back to [] (instead of undefined) when currentStep has no
        // matching formElements entry - e.g. resuming a saved draft whose
        // currentStep is 5 (Review). Without this, currentSection stays
        // undefined until the effect below can correct it, and clicking
        // "Edit" from Review renders CaseCreateFields with field=undefined
        // for one frame, crashing on field.map().
        : (formElements.filter((element) => element.step === currentStep)?.[0]?.sections || [])
    );
    useEffect(() => {
      if(currentStep !== 5)
      {
        if (currentStep === SEVERITY_STEP) {
          setCurrentSection(severitySections[severitySubStep - 1] ? [severitySections[severitySubStep - 1]] : []);
        } else {
          setCurrentSection(formElements.filter((element) => element.step === currentStep)?.[0]?.sections || [])
        }
      }
    }, [currentStep, severitySubStep]);

    // A failed submit can flag fields that live on an earlier step than
    // the one the user is currently on (most submissions fail from the
    // Review & Submit step, step 5, where none of the actual inputs are
    // rendered) - jump to whichever step has the earliest one so the
    // highlighted field (red border + FieldError, same as any other field
    // error) is actually visible instead of just named in a banner.
    // Keyed on submitAttemptId (not submitErrorFields itself) so a repeat
    // failure on the same field still re-triggers the jump even if the
    // user had already navigated away from it.
    useEffect(() => {
      if (!submitErrorFields || !submitErrorFields.length) return;
      let target = null;
      submitErrorFields.forEach((name) => {
        const loc = locateField(name);
        if (!loc) return;
        if (!target || loc.step < target.step || (loc.step === target.step && (loc.subStep || 1) < (target.subStep || 1))) {
          target = loc;
        }
      });
      if (target) {
        setCurrentStep(target.step);
        setSeveritySubStep(target.step === SEVERITY_STEP ? (target.subStep || 1) : 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitAttemptId]);
    const continueToNext = () => {
        setApiError(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });

        if (currentStep === SEVERITY_STEP) {
            const activeSection = severitySections[severitySubStep - 1];
            if (!isFormValid(activeSection ? [activeSection] : [])) {
                setApiError(true);
                return;
            }
            if (severitySubStep < severitySections.length) {
                setSeveritySubStep((prev) => prev + 1);
                // Severity rate's 4 sub-screens are steps in their own
                // right (their own "Continue" click) even though the
                // overall wizard step stays at 4 - autosave here too so
                // progress within severity rate isn't lost.
                saveDraft?.(formValues, currentStep);
                return;
            }
            setCurrentStep((prev) => prev + 1);
            saveDraft?.(formValues, currentStep + 1);
            return;
        }

        if(isFormValid(formElements.filter((element) => element.step === currentStep)?.[0]?.sections))
        {
            setCurrentStep((prev) => prev + 1);
            saveDraft?.(formValues, currentStep + 1);
        }
        else
        {
            setApiError(true)
        }
    }
    const backToPrevious = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setApiError(false);
        if (currentStep === SEVERITY_STEP && severitySubStep > 1) {
            setSeveritySubStep((prev) => prev - 1);
            return;
        }
        setCurrentStep((prev) => prev - 1);
       }

    const content = (
      <>
        {/* The final submit's API-level failure (e.g. daily case limit,
            backend validation) - same FormErrorBanner treatment as
            Signup.jsx/Signin.jsx, shown here below the step header instead
            of floating above the whole page. */}
        <FormErrorBanner>{submitError}</FormErrorBanner>
        {
          apiError &&
          <FormError message={"Please fill out all required fields to continue."}
          subMessage={" Some information is missing or invalid. Review the highlighted fields below before continuing."}
          />
        }
        {
          currentStep === 5 ?
          <ReviewSection formValues={formValues} onEdit={(s) =>  setCurrentStep(s)}
          isAgreed={isAgreed}
          setIsAgreed={setIsAgreed}
          err={err}
          setErr={setErr}
          />
          :
          <CaseCreateFields
          step={currentStep}
          onChangeUpdate={onChangeUpdate}
          formValues ={formValues}
            onBlurValidate={onBlurValidate}
          field = {currentSection}
          errors={errors}
          severitySubStep={severitySubStep}
          severityTitles={severitySections.map((s) => s.title)}
          />
      }
      <CaseCreateFooter
      step={currentStep}
      onContinue={continueToNext}
      onSubmit={() => {
        if(isAgreed) {
          onSubmit();
        }
        else
        {
          setErr(true);
        }
      }}
      onBack={backToPrevious}
      isFormValid={isFormValid}
      />
      </>
    );

  return (
    <div className='w-full bg-cream min-h-full font-plexsans'>

        {isMobile ? (
          <div className='w-full'>
            <CaseCreateHeaderMobile step={currentStep} />
            <div className='bg-ivory px-[18px] pb-6 pt-2'>
              {content}
            </div>
          </div>
        ) : (
          <div className='w-full flex items-stretch'>
            <CaseCreateSidebar
              step={currentStep}
              onStepClick={(s) => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setApiError(false);
                setCurrentStep(s);
              }}
            />
            <div className='flex-1 bg-ivory px-12 py-10 min-w-0'>
              <CaseCreateHeader step={currentStep} />
              {content}
            </div>
          </div>
        )}

    </div>
  )
}

export default CreateCaseForm
